// src/components/CssVariablesControl.tsx
"use client";
import React, { useState, ChangeEvent } from 'react';
import styles from './CssVariablesControl.module.css';
import { CssVariable } from '@interfaces';

const CssVariablesControl: React.FC = () => {
  const variablesColors: CssVariable[] = [
    { name: '--border-color', defaultValue: '#333', type: 'color' },
    { name: '--dropdown-border-color', defaultValue: '#000000', type: 'color' },
    { name: '--dropdown-border-bottom-color', defaultValue: '#ddd', type: 'color' },
    { name: '--link-color', defaultValue: 'white', type: 'color' },
    { name: '--button-color', defaultValue: '#82b74b', type: 'color' },
    { name: '--dropdown-bg', defaultValue: '#f9f9f9', type: 'color' },
    { name: '--dropdown-link-bg', defaultValue: '#ddd', type: 'color' },
    { name: '--dropdown-link-border-color', defaultValue: '#555', type: 'color' },
    { name: '--hover-bg', defaultValue: '#808080', type: 'color' },
    { name: '--active-bg', defaultValue: '#555', type: 'color' },
    { name: '--scrollbar-track-bg', defaultValue: '#f1f1f1', type: 'color' },
    { name: '--scrollbar-thumb-bg', defaultValue: '#888', type: 'color' },
    { name: '--scrollbar-thumb-hover-bg', defaultValue: '#555', type: 'color' },
  ];

  const variablesSliders: CssVariable[] = [
    { name: '--navbar-bg-opacity', defaultValue: '1', type: 'range', min: 0, max: 1, step: 0.1 },
    { name: '--navbar-padding', defaultValue: '10', type: 'range', min: 0, max: 50, step: 1, unit: 'px' },
    { name: '--dropdown-width', defaultValue: '250', type: 'range', min: 100, max: 500, step: 10, unit: 'px' },
    { name: '--button-font-size', defaultValue: '15', type: 'range', min: 10, max: 30, step: 1, unit: 'px' },
    { name: '--link-font-size', defaultValue: '30', type: 'range', min: 10, max: 50, step: 1, unit: 'px' },
    { name: '--dropdown-link-font-size', defaultValue: '20', type: 'range', min: 10, max: 40, step: 1, unit: 'px' },
    { name: '--border-width', defaultValue: '5', type: 'range', min: 1, max: 20, step: 1, unit: 'px' },
    { name: '--scrollbar-width', defaultValue: '8', type: 'range', min: 2, max: 20, step: 1, unit: 'px' },
    { name: '--dropdown-max-height', defaultValue: '225', type: 'range', min: 100, max: 500, step: 10, unit: 'px' },
    { name: '--button-padding', defaultValue: '14', type: 'range', min: 5, max: 30, step: 1, unit: 'px' },
    { name: '--dropdown-link-padding', defaultValue: '12', type: 'range', min: 5, max: 30, step: 1, unit: 'px' },
    { name: '--dropdown-margin', defaultValue: '15', type: 'range', min: 0, max: 30, step: 1, unit: 'px' },
  ];

  const variablesInputs: CssVariable[] = [
    { name: '--primary-bg', defaultValue: 'url(/imgs/grey_background_960_720.png)', type: 'url' },
  ];

  // Define navbar-specific variables
  const navbarVariables = ['--navbar-bg-opacity', '--navbar-padding'];

  const initialValues = [
    ...variablesColors,
    ...variablesSliders,
    ...variablesInputs,
  ].reduce((acc, { name, defaultValue }) => ({ ...acc, [name]: defaultValue }), {});

  const [values, setValues] = useState<Record<string, string>>(initialValues);

  const handleChange = (name: string, type: string, unit?: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const newValue = type === 'url' ? `url(${value})` : type === 'color' ? value : `${value}${unit || ''}`;
    setValues(prev => ({ ...prev, [name]: newValue }));
    document.documentElement.style.setProperty(name, newValue);
  };

  const saveNavbarCss = () => {
    const navbarCss = navbarVariables.reduce((acc, name) => {
      acc[name] = values[name];
      return acc;
    }, {} as Record<string, string>);

    // Save to localStorage
    localStorage.setItem('navbarCss', JSON.stringify(navbarCss));

    // Optional: Provide feedback
    alert('Navbar CSS saved to localStorage!');
  };

  return (
    <div className={styles.controlPanel}>
      {/* Sliders Row */}
      <div className={styles.scrollableRow}>
        {variablesSliders.map(({ name, defaultValue, type, min, max, step, unit }) => (
          <div key={name} className={styles.controlItem}>
            <label htmlFor={name}>{name.replace('--', '')}</label>
            <div className={styles.inputGroup}>
              <input
                type="range"
                id={name}
                min={min}
                max={max}
                step={step}
                value={parseFloat(values[name]) || defaultValue}
                onChange={handleChange(name, type, unit)}
              />
              <input
                type="number"
                min={min}
                max={max}
                step={step}
                value={parseFloat(values[name]) || defaultValue}
                onChange={handleChange(name, type, unit)}
                className={styles.numberInput}
              />
              {unit && <span>{unit}</span>}
            </div>
          </div>
        ))}
      </div>
      {/* Colors and Inputs Row */}
      <div className={styles.scrollableRow}>
        {variablesInputs.map(({ name, defaultValue, type }) => (
          <div key={name} className={styles.controlItem}>
            <label htmlFor={name}>{name.replace('--', '')}</label>
            <input
              type="text"
              id={name}
              value={values[name].replace(/^url\((.*)\)$/, '$1') || defaultValue}
              onChange={handleChange(name, type)}
              placeholder="Enter image URL"
            />
          </div>
        ))}
        {variablesColors.map(({ name, defaultValue, type }) => (
          <div key={name} className={styles.controlItem}>
            <label htmlFor={name}>{name.replace('--', '')}</label>
            <input
              type="color"
              id={name}
              value={values[name] || defaultValue}
              onChange={handleChange(name, type)}
            />
          </div>
        ))}
      </div>
      {/* Save Button */}
      <button className={styles.saveButton} onClick={saveNavbarCss}>
        Save Navbar CSS
      </button>
    </div>
  );
};

export default CssVariablesControl;
